import { Constructor } from './typesctipt.utils';
import { NameVale } from '../dto/commons/NameVale';

export const IgnoreError = (message?: string) => () => !!message && console.warn(message);
export const LogError = (message?: string) => (err: any) => (!!message && console.warn(message)) || console.warn(err);

export const ObjectUtils = {

  flatten: <P, T>(items: P[], iterate: (i: P) => T[]): T[] => {
    return items.reduce((all, item) => all.concat(iterate(item)), [] as T[]);
  },

  assign: <T>(constructor: Constructor<T>, source: Partial<T>): T => {
    return Object.assign<any, Partial<T>>(new constructor(), source);
  },

  cutEmpty: <T extends Object>(o: T): T => {
    if (o === null || Array.isArray(o)) {
      return o;
    }
    Object.keys(o).forEach(key => {
      if (o[key as keyof T] === null || o[key as keyof T] === undefined) {
        delete o[key as keyof T];
      }
    });
    return o;
  },

  dropDuplicatesBy: <T, G>(set: T[], groupFn: (o: T) => G): T[] => {
    const result = [] as T[];
    const keys = Array.from(new Set(set.map(e => groupFn(e))));
    for (const k of keys) {
      result.push(set.find(e => groupFn(e) === k)!);
    }

    return result;
  },

  transpose: <T>(m: T[][]): T[][] => {
    return m?.length ? m[0].map((_, column) => m.map(row => row[column])) : [];
  },

  simpleGroupBy: <T>(set: T[], groupFn: (o: T) => string): T[][] => {
    const map: Record<string, T[]> = {};
    const keys = Array.from(new Set(set.map(e => groupFn(e))));
    for (const k of keys) {
      map[k] = set.filter(e => groupFn(e) === k);
    }
    return Object.values(map);
  },

  unique: <T>(array: T[]): T[] => {
    return Array.from(
      array.reduce((set, e) => set.add(e), new Set<T>())
    )
  },

  /** @deprecated */
  groupBy: <T, G>(set: T[], groupFn: (o: T) => G): T[][] => {
    const map = new Map<G, T[]>();
    const keys = Array.from(new Set(set.map(e => groupFn(e))));
    for (const k of keys) {
      map.set(k, set.filter(e => groupFn(e) === k));
    }

    let combinations: T[][] = [ new Array(keys.length) ];

    let i = 0;
    for (const k of keys) {
      const objects = map.get(k)!;

      const duplicatesBatch: T[][] = [];
      for (const o of objects) {
        const duplicate = combinations.map(c => Array.from(c));
        duplicate.forEach(c => c[i] = o);
        duplicatesBatch.push(...duplicate);
      }
      combinations = duplicatesBatch;
      i++;
    }

    return combinations;
  },

  /** JSON-based: plain objects only (no functions, Dates become strings). */
  deepClone: <T extends Object>(o: T): T => {
    return JSON.parse(JSON.stringify(o));
  },

  normalizeFields: <T extends Object>(o: T, validKeys: string[]): void => {
     for (let v of validKeys) {
       for (let k of Object.keys(o)) {
         if (k.toLowerCase() === v.toLowerCase() && k !== v) {
           o[v as keyof T] = o[k as keyof T];
           delete o[k as keyof T]
         }
       }
     }
  },

  normalizeFieldsBulk: <T extends Object>(objects: T[], validKeys: string[]): void => {
    if (objects.length === 0) {
      return;
    }
    const o = objects[0]
    for (let v of validKeys) {
      for (let k of Object.keys(o)) {
        if (k.toLowerCase() === v.toLowerCase() && k !== v) {
          objects.forEach(o => {
            o[v as keyof T] = o[k as keyof T];
            delete o[k as keyof T]
          })
        }
      }
    }
  },

  uniqueBy<T>(list: T[], uniqueFunction: (item: T) => any): T[] {
    const keys = Array.from(new Set(list.map(uniqueFunction)));
    if (keys.length === list.length) {
      return list;
    }
    const result = [];
    for (const key of keys) {
      const item = list.find(i => uniqueFunction(i) === key);
      if (item) {
        result.push(item);
      }
    }
    return result;
  },

  toNameValueArray(data: Record<any, any>): NameVale[] {
    return Object.keys(data).map(key => ({
      name: data[key],
      value: key
    }));
  }
}
